import { Suspense, useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import { CssVarsProvider, extendTheme } from "@mui/joy/styles";
import CssBaseline from "@mui/joy/CssBaseline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import router from "@/routes";
import "@fontsource/inter";
import "@/styles/global.css";
import { GlobalStyles } from "@mui/joy";
import Stack from "@mui/joy/Stack";
import { Toaster } from "sonner";
import { inject as vercelAnalyticsInject } from "@vercel/analytics";
import LottieLoader from "./components/Loader/LottieLoader";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { TrackJS } from "trackjs";
import { ClerkProvider, useAuth, useClerk } from "@clerk/react";
import { setClerk } from "@/services/auth/clerk";

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

// Hands the Clerk instance to non-React code (axios) before any route renders and fetches.
// Also reloads when the signed-in Clerk user goes away or changes without our signOut()
// (e.g. signed out or switched account in another tab), so the previous user's Zustand
// state and cached queries (staleTime: Infinity) can't leak into the next session.
const ClerkBridge = ({ children }: { children: React.ReactNode }) => {
  setClerk(useClerk());

  const { isLoaded, userId } = useAuth();
  const lastUserId = useRef<string | null>(null);
  const userChanged = isLoaded && lastUserId.current !== null && userId !== lastUserId.current;

  useEffect(() => {
    if (!isLoaded) return;

    if (userChanged) {
      window.location.assign("/auth/login");
      return;
    }

    lastUserId.current = userId ?? null;
  }, [isLoaded, userId, userChanged]);

  // don't render the old user's pages while the reload is pending
  if (userChanged) return null;

  return <>{children}</>;
};

TrackJS.install({
  token: import.meta.env.VITE_TRACKJS_TOKEN,
  application: "hub-client",
});

const MAX_RETRY = 1;
const STATUS_CODE_RETRY_THRESHOLD = 500;

ReactGA.initialize(import.meta.env.VITE_GA_ID);
// ReactGA.send({ hitType: "pageview", page: window.location.pathname });

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.5, // tracesSampleRate to 0.5 to capture 50%
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/hub.service.prod.useast1.ttand.com\/api/],
});

vercelAnalyticsInject();

// joy ui theme extending
const theme = extendTheme({
  cssVarPrefix: "ttand",
  colorSchemes: {
    light: {
      palette: {
        primary: {
          // "50": "#fff7ed",
          // "100": "#ffedd5",
          // "200": "#fed7aa",
          // "300": "#fdba74",
          // "400": "#fb923c",
          // "500": "#f97316",
          // "600": "#ea580c",
          // "700": "#c2410c",
          // "800": "#9a3412",
          // "900": "#7c2d12",
          "50": "#fff7ed",
          "100": "#ffedd5",
          "200": "#fed7aa",
          "300": "#fdba74",
          "400": "#fb923c",
          "500": "#f97316",
          "600": "#ea580c",
          "700": "#c2410c",
          "800": "#9a3412",
          "900": "#7c2d12",
        },
        success: {
          "50": "#f0fdf4",
          "100": "#dcfce7",
          "200": "#bbf7d0",
          "300": "#86efac",
          "400": "#4ade80",
          // "500": "#22c55e",
          "500": "#16a34a",
          "600": "#16a34a",
          "700": "#15803d",
          "800": "#166534",
          "900": "#14532d",
        },
        danger: {
          "50": "#fef2f2",
          "100": "#fee2e2",
          "200": "#fecaca",
          "300": "#fca5a5",
          "400": "#f87171",
          "500": "#ef4444",
          "600": "#dc2626",
          "700": "#b91c1c",
          "800": "#991b1b",
          "900": "#7f1d1d",
        },
      },
    },
  },
  components: {
    JoyCard: {
      styleOverrides: {
        root: {
          backgroundColor: "white",
        },
      },
    },
    JoyDialogTitle: {
      styleOverrides: {
        root: {
          color: "#171A1C",
        },
      },
    },
    JoyInput: {
      styleOverrides: {
        root: {
          borderWidth: "1px",
          padding: "8px",
          "--Input-focusedThickness": "1px",
        },
      },
    },
    JoyTab: {
      styleOverrides: {
        root: {
          fontSize: "15px",
        },
      },
    },
    JoyTextarea: {
      styleOverrides: {
        root: {
          borderWidth: "1px",
          padding: "8px",
          "--Textarea-focusedThickness": "1px",
        },
      },
    },
    JoyAutocomplete: {
      styleOverrides: {
        root: {
          borderWidth: "1px",
          "--Input-focusedThickness": "1px",
        },
      },
    },
  },
});

// tanstack query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: (failureCount, error: any) => {
        if (failureCount + 1 >= MAX_RETRY) return false;

        if (error?.response?.status && error.response.status >= STATUS_CODE_RETRY_THRESHOLD) return true;

        return false;
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  // <React.StrictMode>
  <CssVarsProvider theme={theme}>
    <CssBaseline />

    <QueryClientProvider client={queryClient}>
      {/* toast */}
      <GlobalStyles
        styles={(theme) => `
        [data-sonner-toaster][data-theme] {
          font-family: ${theme.vars.fontFamily.body};
          font-size: ${theme.fontSize.md};
          --border-radius: ${theme.vars.radius.sm};
          --normal-bg: ${theme.vars.palette.background.surface};
          --normal-border: ${theme.vars.palette.divider};
          --normal-text: ${theme.vars.palette.text.primary};
          --success-bg: ${theme.vars.palette.success.softBg};
          --success-border: rgb(${theme.vars.palette.success.mainChannel} / 0.2);
          --success-text: ${theme.vars.palette.success.softColor};
          --error-bg: ${theme.vars.palette.danger.softBg};
          --error-border: rgb(${theme.vars.palette.danger.mainChannel} / 0.2);
          --error-text: ${theme.vars.palette.danger.softColor};
          --gray1: ${theme.vars.palette.neutral[50]};
          --gray2: ${theme.vars.palette.neutral[100]};
          --gray3: ${theme.vars.palette.neutral[200]};
          --gray4: ${theme.vars.palette.neutral[300]};
          --gray5: ${theme.vars.palette.neutral[400]};
          --gray6: ${theme.vars.palette.neutral[500]};
          --gray7: ${theme.vars.palette.neutral[600]};
          --gray8: ${theme.vars.palette.neutral[700]};
          --gray9: ${theme.vars.palette.neutral[800]};
          --gray10: ${theme.vars.palette.neutral[900]};
        }
        &.sonner-toast-warn {
          --normal-bg: ${theme.vars.palette.warning.softBg};
          --normal-border: rgb(${theme.vars.palette.warning.mainChannel} / 0.2);
          --normal-text: ${theme.vars.palette.warning.softColor};
        }
      `}
      />
      <Toaster position="top-right" richColors closeButton expand />

      <Suspense
        fallback={
          <Stack
            alignItems={"center"}
            justifyContent={"center"}
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <LottieLoader />
          </Stack>
        }
      >
        <ClerkProvider
          publishableKey={CLERK_PUBLISHABLE_KEY}
          routerPush={(to) => router.navigate(to)}
          routerReplace={(to) => router.navigate(to, { replace: true })}
          signInUrl="/auth/login"
          telemetry={false}
        >
          <ClerkBridge>
            <RouterProvider router={router} />
          </ClerkBridge>
        </ClerkProvider>
      </Suspense>
    </QueryClientProvider>
  </CssVarsProvider>,
  // </React.StrictMode>,
);
